import { useCallback, useEffect, useMemo, useRef } from 'react';
import type {
  DialogueStickerMessage,
  DialogueTextMessage,
  DialogueUnknowMessage,
  DialogueVideoMessage,
  DialogueItemMessage,
  DialogueOrderMessage,
  DialogueImageMessage,
  DialogueBaseMessage,
  DialogueSystemMessage,
  DialogueFollowMessage,
  DialogueVoucherMessage,
  DialogueRefundMessage,
  DialogueTextVideoMessage,
  DialogueSbsTradeInfoMessage,
  DialogueOfferMessage,
  DialogueTemplateMessage,
  DialogueBundleMessageMessage,
  DialogueImageWithTextMessage,
  DialogueLazadaOfferMessage,
  DialogueAttachmentMessage,
  DialogueKwNoticeMessage,
  DialogueKwPrologueMessage,
  DialogueKwFaqMessage,
  DialogueKwOrderCardMessage,
  DialogueKwPaymentCardMessage,
  DialogueKwEvaluationMessage,
  DialogueReturnRefundCardMessage,
  DialogueFbMessage,
  DialogueTtsLogisticsCardMessage,
  DialogueVariationCardMessage,
  DialogueJolectAttachmentMessage,
  DialogueJolectFixedReplyMessage,
  DialogueJolectAiGuideMessage,
  DialogueJolectFormMessage,
} from 'components/Dialogue/types';
import type { LocalMessage } from 'hooks/business/conversation';
import useCurrentChat from 'hooks/business/im/useCurrentChat';
import { useSystemLanguageValue } from 'hooks/business/system/useSystem';
import { imageUrlGenerator, videoUrlGenerator } from 'utils/shopee/urlGenerator';
import { useTranslate } from '@tolgee/react';
import { toNumber } from 'lodash-es';
import sysLangToTranslationLang from 'utils/sysLangToTranslationLang';
import dayjs from 'lib/dayjs';
import useTipTrigger, {
  useTipManualClose,
  useTipAutoClose,
} from 'views/Conversation/Chat/components/Remind/tip';
import { useChatBuyerLang } from './hooks/useChatBuyerLang';
import { useMessagesAutoTranslate } from './components/MessageEditor/Toolbar/MessagesAutoTranslate';
import { useOrgConfigStateValue } from 'hooks/business/useOrgConfig';
import { QIFeature } from 'utils/featureFlag';
import { useReplaceReason } from 'views/Setting/BlockList/components/useOfficialReason';
import { useSingleOrBatchTrans } from 'hooks/business/im/useSingleOrBatchTrans';
import { useLocation } from 'react-router-dom';
import { getJolectAiGuideMarkdown } from './utils';

const getMessageStatus = (raw: LocalMessage): DialogueBaseMessage['status'] => {
  const localStatus = raw?.localStatus || '';
  let status: DialogueBaseMessage['status'] = 'normal';
  status =
    ['loading', 'processing'].includes(localStatus) || raw.status === 'PENDING'
      ? 'loading'
      : status;
  status =
    ['error', 'ack_timeout'].includes(localStatus) || raw.status === 'FAIL' ? 'error' : status;
  return status;
};

export interface UIMessages extends DialogueBaseMessage {
  raw: LocalMessage;
}

/**
 * 咨询订单/商品消息处理
 * 连续咨询消息取第一条消息显示卡片, 其余只显示文本
 */
function sourceContentMessageTransformer(curr: LocalMessage, prev?: LocalMessage) {
  if (!curr?.sourceContent || !prev || !prev.sourceContent) return curr;
  const { sourceContent: currSourceContent, ...currProps } = curr;
  const { sourceContent: prevSourceContent } = prev;
  if (currSourceContent.sourceContentType === prevSourceContent.sourceContentType) {
    // 对比前后咨询消息是否一致
    // 如果一致则后一条消息不再返回 sourceContent 对象, UI 将不会渲染卡片
    if (
      (currSourceContent.sourceContentType === 'ORDER' &&
        currSourceContent.externalOrderSn === prevSourceContent.externalOrderSn) ||
      (currSourceContent.sourceContentType === 'ITEM' &&
        currSourceContent.externalItemId === prevSourceContent.externalItemId)
    ) {
      return currProps;
    }
  }
  return curr;
}

export function getThumb(
  channel: Conversation.Message['channel'],
  region: General.CaselessRegion,
  thumbUrl?: string,
) {
  const isValidThumb = thumbUrl?.startsWith('blob:') || thumbUrl?.startsWith('https://');

  if (isValidThumb || !thumbUrl) {
    return thumbUrl;
  }

  if (channel === 'SHOPEE') {
    return imageUrlGenerator(region, thumbUrl);
  }

  return thumbUrl;
}

export function getUrl(
  channel: Conversation.Message['channel'],
  region: General.CaselessRegion,
  videoUrl?: string,
) {
  const isValidUrl = videoUrl?.startsWith('blob:') || videoUrl?.startsWith('https://');

  if (isValidUrl) {
    return videoUrl;
  }

  if (channel === 'SHOPEE') {
    return videoUrlGenerator(region, videoUrl || '');
  }

  return videoUrl || '';
}

export function useUiMessage({
  messages,
  conversation,
}: {
  messages: LocalMessage[];
  conversation: Conversation.Conversation;
}) {
  const { t } = useTranslate();

  const tipTrigger = useTipTrigger();
  const tipManualClose = useTipManualClose();
  const tipAutoClose = useTipAutoClose();
  const replaceBlacklistReason = useReplaceReason();

  const uiMessages = useMemo(() => {
    const messageTransformer = (
      raw: LocalMessage,
      region: General.CaselessRegion,
      prevRow?: LocalMessage,
    ) => {
      const fromType = raw.msgFromType;
      const isBuyerMessage = fromType === 'BUYER' || fromType === 'MERCADO2ASSISTANT';
      const isRobotMesssage = fromType === 'BOT';
      let sender: DialogueBaseMessage['sender'] = 'seller';
      sender = isBuyerMessage ? 'buyer' : sender;
      sender = isRobotMesssage ? 'robot' : sender;
      // const nickName = isBuyerMessage ? raw.fromUserName : raw.fromUserName || assinstantName;
      // 客服名: 无`fromUserName`时优先使用店铺备注名,或店铺名;
      const nickName = raw.fromUserName;
      const localStatus = raw?.localStatus;
      const status = getMessageStatus(raw);
      const basicMsg = {
        dialogueUuid: raw.msgId,
        time: parseInt(raw.sendTime as string, 10),
        nickName,
        sender,
        status,
        raw,
      } as UIMessages;
      switch (raw.messageType) {
        /**
         * FAQ_LIVEAGENT 按官方设定为普通买家消息
         * 展示形式与买家消息保持一致
         */
        case 'FAQ_LIVEAGENT': {
          const newRaw = sourceContentMessageTransformer(raw, prevRow);
          const text = t('domain.message.type.faqLiveAgent');
          const msg: DialogueTextMessage = {
            ...basicMsg,
            type: 'text',
            content: {
              text,
            },
            raw: {
              ...newRaw,
            },
          };
          return msg;
        }
        case 'TEXT': {
          const newRaw = sourceContentMessageTransformer(raw, prevRow);
          const content = newRaw.textContent as Conversation.MsgText;
          const msg: DialogueTextMessage = {
            ...basicMsg,
            type: 'text',
            content: {
              text: content.text,
            },
            raw: {
              ...newRaw,
              failCause: localStatus === 'error' ? newRaw.failCause : content?.failCause,
            },
          };
          return msg;
        }
        case 'TEMPLATE': {
          const content = raw.templateContent as Conversation.MsgTemplate;
          const msg: DialogueTemplateMessage = {
            ...basicMsg,
            type: 'template',
            raw: {
              ...raw,
              failCause: localStatus === 'error' ? raw.failCause : content?.failCause,
            },
          };
          return msg;
        }
        case 'BUNDLE_MESSAGE': {
          const content = raw.bundleMessageContent as Conversation.MsgBundleMessage;
          const msg: DialogueBundleMessageMessage = {
            ...basicMsg,
            type: 'bundleMessage',
            raw: {
              ...raw,
              failCause: localStatus === 'error' ? raw.failCause : content?.failCause,
            },
          };
          return msg;
        }
        case 'IMAGE_WITH_TEXT': {
          const { text, imageUrl, thumbWidth, thumbHeight, translationContent, failCause } =
            raw.imageWithTextContent as Conversation.MsgImageWithText;

          const msg: DialogueImageWithTextMessage = {
            ...basicMsg,
            type: 'imageWithText',
            raw: {
              ...raw,
              textContent: {
                text,
                originalText: text,
                translationContent,
                ...raw.textContent,
              },
              imageContent: {
                imgUrl: imageUrl,
                externalImgUrl: imageUrl,
                width: thumbWidth,
                height: thumbHeight,
                ...raw.imageContent,
              },
              failCause: localStatus === 'error' ? raw.failCause : failCause,
            },
          };
          return msg;
        }
        case 'VIDEO': {
          const { videoContent: content, channel } = raw;
          const msg = {
            ...basicMsg,
            type: 'video',
            content: {
              thumb: getThumb(channel, region, content?.thumbUrl),
              url: getUrl(channel, region, content?.videoUrl),
            },
            raw: {
              ...raw,
              failCause: localStatus === 'error' ? raw.failCause : content?.failCause,
            },
          } as DialogueVideoMessage;
          return msg;
        }
        case 'IMAGE': {
          const content = raw.imageContent;
          const msg = {
            ...basicMsg,
            type: 'image',
            content: {
              url: content?.externalImgUrl || content?.imgUrl,
            },
            raw: {
              ...raw,
              failCause: localStatus === 'error' ? raw.failCause : content?.failCause,
            },
          } as DialogueImageMessage;

          return msg;
        }
        case 'STICKER': {
          const content = raw.stickerContent as Conversation.MsgSticker;
          const msg = {
            ...basicMsg,
            type: 'sticker',
            content: {
              stickerId: content.stickerId,
              packageId: content.stickerPackageId,
            },
            raw: {
              ...raw,
              failCause: localStatus === 'error' ? raw.failCause : content?.failCause,
            },
          } as DialogueStickerMessage;
          return msg;
        }
        case 'ITEM': {
          const content = raw.itemContent;
          const msg = {
            ...basicMsg,
            type: 'item',
            content: {
              link: '', // get it from remote
              id: content?.externalItemId,
              title: '', // get it from remote,
              img: '', // get it from remote,
            },
            raw: {
              ...raw,
              failCause: localStatus === 'error' ? raw.failCause : content?.failCause,
            },
          } as DialogueItemMessage;
          return msg;
        }
        case 'ORDER': {
          const content = raw.orderContent;
          const msg = {
            ...basicMsg,
            type: 'order',
            content: {
              id: content?.orderSn,
              invoiceNumber: content?.orderSn,
              externalBuyerId: '',
              internalBuyerId: 0,
              createTime: new Date().valueOf(), // get it from remote,
              total: 0, // get it from remote,
              items: [], // get it from remote,
            },
            raw: {
              ...raw,
              failCause: localStatus === 'error' ? raw.failCause : content?.failCause,
            },
          } as DialogueOrderMessage;
          return msg;
        }
        case 'SYS_TRANSFER': {
          const { fromUserName, toUserName, sendTime } = raw;
          const remark = raw.sysTransferContent?.remark;
          const isSuperviser = raw.sysTransferContent?.superviseTransfer;
          const sendTimeText = dayjs(toNumber(sendTime)).format('YYYY-MM-DD HH:mm:ss');
          const msg = {
            ...basicMsg,
            type: 'system',
            content: {
              text: t(
                isSuperviser
                  ? fromUserName
                    ? 'ViewsChat.message.system.supervise.forward'
                    : 'ViewsChat.message.system.forward'
                  : 'ViewsChat.message.system.forward',
                {
                  to: toUserName,
                  from: fromUserName || t('domain.department.role.superAdmin'),
                  time: `${sendTimeText} `,
                },
              ),
              extra: remark
                ? t('scene.chatWorkspace.messageThread.systemMessage.forwardRemark', {
                    content: remark,
                  })
                : undefined,
            },
          } as DialogueSystemMessage;
          return msg;
        }
        case 'SYS_REMIND_TRIGGER': {
          const {
            scene = '',
            waitReply,
            condition = '',
            timestamp,
          } = raw.sysRemindTriggerContent || {};
          const msg = {
            ...basicMsg,
            type: 'system',
            content: {
              text: `${dayjs(Number(timestamp)).format('YYYY-MM-DD HH:mm:ss')} ${tipTrigger(
                scene,
                condition,
                waitReply,
              )}`,
            },
          } as DialogueSystemMessage;
          return msg;
        }
        case 'SYS_REMIND_MANUAL_CLOSE': {
          const {
            trigger = [],
            solved = false,
            timestamp,
            assistantName = '',
          } = raw.sysRemindManualCloseContent || {};
          const msg = {
            ...basicMsg,
            type: 'system',
            content: {
              text: `${dayjs(Number(timestamp)).format('YYYY-MM-DD HH:mm:ss')} ${tipManualClose(
                assistantName,
                solved,
                trigger,
              )}`,
            },
          } as DialogueSystemMessage;
          return msg;
        }
        case 'SYS_REMIND_AUTO_CLOSE': {
          const {
            trigger,
            timeout = false,
            condition = '',
            timestamp,
          } = raw.sysRemindAutoCloseContent || {};
          const { scene = '', condition: trigCond = '' } = trigger || {};
          const msg = {
            ...basicMsg,
            type: 'system',
            content: {
              text: `${dayjs(Number(timestamp)).format('YYYY-MM-DD HH:mm:ss')} ${tipAutoClose(
                scene,
                timeout,
                condition,
                trigCond,
              )}`,
            },
          } as DialogueSystemMessage;
          return msg;
        }
        case 'SYS_PAUSE_AI_EXPIRE':
        case 'SYS_PAUSE_AI_STOP':
        case 'SYS_PAUSE_AI_START':
        case 'SYS_PAUSE_MK_RR_EXPIRE':
        case 'SYS_PAUSE_MK_RR_STOP':
        case 'SYS_PAUSE_MK_RR_START': {
          const who = raw?.fromUserName;
          const minutes = raw?.sysPauseAiContent?.minutes || 0;
          const duration =
            minutes < 60
              ? `${minutes}${t('domain.time.type.minute')}`
              : `${Math.ceil(minutes / 60)}${t('domain.time.type.hour')}`;

          const options = {
            who,
            duration,
          };

          const msg = {
            ...basicMsg,
            type: 'system',
            content: {
              text:
                raw.messageType === 'SYS_PAUSE_AI_START'
                  ? t('scene.chatWorkspace.messageThread.systemMessage.pauseAiStarted', options)
                  : raw.messageType === 'SYS_PAUSE_AI_STOP'
                  ? t('scene.chatWorkspace.messageThread.systemMessage.pauseAiStopped', options)
                  : raw.messageType === 'SYS_PAUSE_AI_EXPIRE'
                  ? t('scene.chatWorkspace.messageThread.systemMessage.pauseAiExpired', options)
                  : t(`ViewsChat.${raw.messageType}`, options),
            },
          } as DialogueSystemMessage;
          return msg as DialogueSystemMessage;
        }
        case 'FOLLOWER': {
          const content = raw.followerContent as Conversation.MsgFollower;
          return {
            ...basicMsg,
            type: 'follow',
            raw: {
              ...raw,
              failCause: localStatus === 'error' ? raw.failCause : content?.failCause,
            },
          } as DialogueFollowMessage;
        }
        case 'VOUCHER': {
          const content = raw.voucherContent as Conversation.MsgVoucher;
          return {
            ...basicMsg,
            type: 'voucher',
            raw: {
              ...raw,
              failCause: localStatus === 'error' ? raw.failCause : content?.failCause,
            },
          } as DialogueVoucherMessage;
        }
        case 'REFUND': {
          return {
            ...basicMsg,
            type: 'refund',
          } as DialogueRefundMessage;
        }
        case 'TEXT_VIDEO': {
          return {
            ...basicMsg,
            type: 'textVideo',
            content: raw,
            raw: {
              ...raw,
              failCause: localStatus === 'error' ? raw.failCause : raw.textVideoContent?.failCause,
            },
          } as DialogueTextVideoMessage;
        }
        case 'SBS_TRADE_INFO': {
          return {
            ...basicMsg,
            type: 'sbsTradeInfo',
            content: raw,
          } as DialogueSbsTradeInfoMessage;
        }
        case 'OFFER': {
          return {
            ...basicMsg,
            type: 'offer',
            content: raw.offerContent,
            raw,
          } as DialogueOfferMessage;
        }
        case 'LAZADA_OFFER': {
          return {
            ...basicMsg,
            type: 'lazadaOffer',
            content: raw.lazadaOfferContent,
            raw,
          } as DialogueLazadaOfferMessage;
        }
        case 'SYS_BLACKLIST': {
          const { textContent } = raw;
          let content;
          try {
            content = replaceBlacklistReason(
              textContent?.translationContent?.enUS?.text ?? '',
              raw.sendTime,
            );
          } catch (error) {
            //
          }
          const msg = {
            ...basicMsg,
            type: 'system',
            content: {
              text: content,
            },
          } as DialogueSystemMessage;
          return msg;
        }
        case 'SYS_ASSIGN_CONTEXT': {
          const { sysAssignContextContent } = raw;

          if (!sysAssignContextContent) {
            break;
          }

          const { timestamp, assignSource, toAssistantDest, forceContactBuyerSource } =
            sysAssignContextContent;

          if (assignSource !== 'FORCE_CONTACT_BUYER') {
            return {
              ...basicMsg,
              type: 'system',
              content: {
                text: t('scene.chatWorkspace.messageThread.systemMessage.assignedContext', {
                  time: dayjs(Number(timestamp)).format('MM-DD HH:mm:ss'),
                  nickname: toAssistantDest.assistantName,
                }),
              },
            } as DialogueSystemMessage;
          }

          const { action, assistantName } = forceContactBuyerSource;

          const sourceKeyMap: Record<typeof action, string> = {
            TODO: 'scene.chatWorkspace.assignmentHistory.event.contactBuyerFromTask',
            SEARCH_ORDER: 'scene.chatWorkspace.assignmentHistory.event.contactBuyerBySearch',
            ORDER_MANAGEMENT: 'scene.chatWorkspace.assignmentHistory.event.contactBuyerFromOrder',
            RATING: 'scene.chatWorkspace.assignmentHistory.event.contactBuyerFromReview',
            UNKNOWN_ACTION: 'scene.chatWorkspace.assignmentHistory.event.contactBuyer',
          };

          const { [action]: i18nKey = 'scene.chatWorkspace.assignmentHistory.event.contactBuyer' } =
            sourceKeyMap;

          return {
            ...basicMsg,
            type: 'system',
            content: {
              text: `${dayjs(Number(timestamp)).format('MM-DD HH:mm:ss')} ${t(i18nKey, {
                nickname: assistantName,
              })}`,
            },
          } as DialogueSystemMessage;
        }

        case 'ATTACHMENT': {
          const { attachmentContent } = raw;
          return {
            ...basicMsg,
            type: 'attachment',
            content: attachmentContent,
            raw: {
              ...raw,
              failCause: localStatus === 'error' ? raw.failCause : attachmentContent?.failCause,
            },
          } as DialogueAttachmentMessage;
        }
        case 'KW_NOTICE': {
          const { kwNoticeContent } = raw;
          return {
            ...basicMsg,
            type: 'kwNotice',
            content: kwNoticeContent,
            raw: {
              ...raw,
              failCause: localStatus === 'error' ? raw.failCause : kwNoticeContent?.failCause,
            },
          } as DialogueKwNoticeMessage;
        }
        case 'KW_PROLOGUE': {
          const { kwPrologueContent } = raw;
          return {
            ...basicMsg,
            type: 'kwPrologue',
            content: kwPrologueContent,
            raw: {
              ...raw,
              failCause: localStatus === 'error' ? raw.failCause : kwPrologueContent?.failCause,
            },
          } as DialogueKwPrologueMessage;
        }
        case 'KW_FAQ': {
          const { kwFaqContent } = raw;
          return {
            ...basicMsg,
            type: 'kwFaq',
            content: kwFaqContent,
            raw: {
              ...raw,
              failCause: localStatus === 'error' ? raw.failCause : kwFaqContent?.failCause,
            },
          } as DialogueKwFaqMessage;
        }
        case 'KW_REMIND_ORDER': {
          const { kwOrderCardMessageContent } = raw;
          return {
            ...basicMsg,
            type: 'kwOrderCard',
            content: kwOrderCardMessageContent,
            raw: {
              ...raw,
              failCause:
                localStatus === 'error' ? raw.failCause : kwOrderCardMessageContent?.failCause,
            },
          } as DialogueKwOrderCardMessage;
        }
        case 'KW_REMIND_PAYMENT': {
          const { kwPaymentCardMessageContent } = raw;
          return {
            ...basicMsg,
            type: 'kwPaymentCard',
            content: kwPaymentCardMessageContent,
            raw: {
              ...raw,
              failCause:
                localStatus === 'error' ? raw.failCause : kwPaymentCardMessageContent?.failCause,
            },
          } as DialogueKwPaymentCardMessage;
        }
        case 'KW_EVALUATION': {
          const { kwEvaluationMessageContent } = raw;
          return {
            ...basicMsg,
            type: 'kwEvaluation',
            content: kwEvaluationMessageContent,
            raw: {
              ...raw,
              failCause:
                localStatus === 'error' ? raw.failCause : kwEvaluationMessageContent?.failCause,
            },
          } as DialogueKwEvaluationMessage;
        }
        case 'RETURN_REFUND_CARD': {
          const { returnRefundCardContent } = raw;
          return {
            ...basicMsg,
            type: 'returnRefundCard',
            content: returnRefundCardContent,
            raw: {
              ...raw,
              failCause:
                localStatus === 'error' ? raw.failCause : returnRefundCardContent?.failCause,
            },
          } as DialogueReturnRefundCardMessage;
        }
        case 'FACEBOOK_MESSAGE': {
          const { fbMsgContent } = raw;

          return {
            ...basicMsg,
            type: 'facebook',
            content: fbMsgContent,
            raw: {
              ...raw,
              failCause: localStatus === 'error' ? raw.failCause : fbMsgContent?.failCause,
            },
          } as DialogueFbMessage;
        }
        case 'TTS_LOGISTICS_CARD': {
          const { ttsLogisticsCardContent } = raw;

          return {
            ...basicMsg,
            type: 'ttsLogisticsCard',
            content: ttsLogisticsCardContent,
            raw: {
              ...raw,
              failCause:
                localStatus === 'error' ? raw.failCause : ttsLogisticsCardContent?.failCause,
            },
          } as DialogueTtsLogisticsCardMessage;
        }
        case 'VARIATION_CARD': {
          const { variationCardContent } = raw;

          return {
            ...basicMsg,
            type: 'variationCard',
            content: variationCardContent,
            raw: {
              ...raw,
              failCause: localStatus === 'error' ? raw.failCause : variationCardContent?.failCause,
            },
          } as DialogueVariationCardMessage;
        }
        case 'JL_ATTACHMENT': {
          const { jlAttachmentContent } = raw;
          return {
            ...basicMsg,
            type: 'jolectAttachment',
            content: jlAttachmentContent,
            raw: {
              ...raw,
              failCause: localStatus === 'error' ? raw.failCause : jlAttachmentContent?.failCause,
            },
          } as DialogueJolectAttachmentMessage;
        }
        case 'JL_FIXED_REPLY': {
          const { jlFixedReplyContent } = raw;
          return {
            ...basicMsg,
            type: 'jolectFixedReply',
            content: jlFixedReplyContent,
            raw: {
              ...raw,
              failCause: localStatus === 'error' ? raw.failCause : jlFixedReplyContent?.failCause,
            },
          } as DialogueJolectFixedReplyMessage;
        }
        case 'JL_AI_GUIDE': {
          const { jlAiGuideContent } = raw;
          return {
            ...basicMsg,
            type: 'jolectAiGuide',
            content: jlAiGuideContent,
            raw: {
              ...raw,
              failCause: localStatus === 'error' ? raw.failCause : jlAiGuideContent?.failCause,
            },
          } as DialogueJolectAiGuideMessage;
        }
        case 'JL_FORM': {
          const { jlFormContent } = raw;
          return {
            ...basicMsg,
            type: 'jolectForm',
            content: jlFormContent,
            raw: {
              ...raw,
              failCause: localStatus === 'error' ? raw.failCause : jlFormContent?.failCause,
            },
          } as DialogueJolectFormMessage;
        }
        case 'JL_SYSTEM_MSG': {
          return {
            ...basicMsg,
            type: 'system',
            content: {
              text: '',
            },
            raw,
          } as DialogueSystemMessage;
        }
        case 'FIRST_MSG_ITEM_RCMD': {
          const { firstMsgItemRcmdContent } = raw;

          const msg = {
            ...basicMsg,
            type: 'item',
            content: {
              link: '', // get it from remote
              id: firstMsgItemRcmdContent?.itemCardV2s?.[0]?.itemId,
              title: '', // get it from remote,
              img: '', // get it from remote,
            },
            raw: {
              ...raw,
              failCause:
                localStatus === 'error' ? raw.failCause : firstMsgItemRcmdContent?.failCause,
            },
          } as DialogueItemMessage;
          return msg;
        }

        default: {
          const msg = {
            ...basicMsg,
            type: 'unknown',
          } as DialogueUnknowMessage;
          return msg;
        }
      }
    };

    return messages
      .map((msg, index) =>
        messageTransformer(
          msg,
          conversation?.region || 'id',
          index > 0 ? messages[index - 1] : undefined,
        ),
      )
      .filter((_) => _);
  }, [
    messages,
    t,
    tipTrigger,
    tipManualClose,
    tipAutoClose,
    replaceBlacklistReason,
    conversation?.region,
  ]);

  return {
    uiMessages,
    t,
    tipTrigger,
    tipManualClose,
    tipAutoClose,
    replaceBlacklistReason,
  };
}

export function useGetNoTransedMsgs(conversation: Conversation.Conversation) {
  const messagesAutoTranslate = useMessagesAutoTranslate();
  const preferBuyerLang = useChatBuyerLang(conversation);
  const sysLang = useSystemLanguageValue();
  const isSameLang = preferBuyerLang === sysLang;

  const { whiteListConfig: { SELLER_MSG_AUTO_TRANSLATE = false, IGNORE_SHOPEE_TRANSLATION } = {} } =
    useOrgConfigStateValue();

  const { pathname } = useLocation();

  const getNoTransedMsgs = useCallback(
    ({
      messages,
      translateEffect = true,
    }: {
      messages: LocalMessage[];
      translateEffect?: boolean;
    }) => {
      if ((QIFeature ? false : !messagesAutoTranslate) || isSameLang || !translateEffect) {
        // 质检翻译常开
        return [];
      }

      return messages.reduce<LocalMessage[]>((res, msg) => {
        const {
          msgFromType,
          textContent,
          messageType,
          voucher,
          imageWithTextContent,
          attachmentContent,
          jlFormContent,
          jlFixedReplyContent,
          jlAiGuideContent,
        } = msg;

        const typesMatched =
          ((QIFeature ? true : SELLER_MSG_AUTO_TRANSLATE) ||
            msgFromType === 'BUYER' ||
            pathname.startsWith('/knowledge')) &&
          ((textContent?.text && messageType === 'TEXT') ||
            (messageType === 'IMAGE_WITH_TEXT' && imageWithTextContent?.text) ||
            (messageType === 'ATTACHMENT' && attachmentContent?.text) ||
            (messageType === 'JL_FORM' && jlFormContent?.text) ||
            (messageType === 'JL_FIXED_REPLY' && jlFixedReplyContent?.text) ||
            (messageType === 'JL_AI_GUIDE' && getJolectAiGuideMarkdown(jlAiGuideContent))) &&
          voucher !== true;

        if (!typesMatched) {
          return res;
        }

        let transed: string | undefined = '';
        // 质检不关心originalText
        if (!QIFeature) {
          if (textContent?.originalText) {
            transed = textContent?.originalText;
          } else {
            let content;
            if (msg.messageType === 'IMAGE_WITH_TEXT') {
              content = msg.imageWithTextContent;
            } else if (msg.messageType === 'ATTACHMENT') {
              content = msg.attachmentContent;
            } else if (msg.messageType === 'JL_FORM') {
              content = msg.jlFormContent;
            } else if (msg.messageType === 'JL_FIXED_REPLY') {
              content = msg.jlFixedReplyContent;
            } else if (msg.messageType === 'JL_AI_GUIDE') {
              content = msg.jlAiGuideContent;
            } else {
              content = msg.textContent;
            }
            const translation = content?.translationContent?.[sysLangToTranslationLang(sysLang)];
            if (
              msg.channel !== 'SHOPEE' ||
              !IGNORE_SHOPEE_TRANSLATION ||
              translation?.source !== 'shopee'
            ) {
              transed = translation?.text;
            }
          }
        }

        if (!transed) {
          res.push(msg);
        }

        return res;
      }, []);
    },
    [
      IGNORE_SHOPEE_TRANSLATION,
      SELLER_MSG_AUTO_TRANSLATE,
      isSameLang,
      messagesAutoTranslate,
      sysLang,
      pathname,
    ],
  );

  return {
    messagesAutoTranslate,
    preferBuyerLang,
    sysLang,
    isSameLang,
    getNoTransedMsgs,
  };
}

const useChatView = (
  iframeMode: boolean = false,
  onMercadoChatView: boolean = false,
  translateEffect = true,
  enableNouseMessage = false,
  claimId?: string,
) => {
  const {
    messages,
    keyMsgId,
    conversation,
    fetch,
    fetchSbs,
    preference,
    setPreference,
    nouseMsgList,
  } = useCurrentChat(iframeMode, onMercadoChatView, enableNouseMessage, claimId);
  const singleOrBatchTrans = useSingleOrBatchTrans();

  const { uiMessages } = useUiMessage({
    messages,
    conversation,
  });

  const { preferBuyerLang, sysLang, isSameLang, getNoTransedMsgs } =
    useGetNoTransedMsgs(conversation);

  useEffect(() => {
    const nonTransed = getNoTransedMsgs({
      messages: refs.current.messages,
      translateEffect,
    });
    if (nonTransed.length) {
      refs.current.singleOrBatchTrans(nonTransed, sysLang);
    }
  }, [getNoTransedMsgs, sysLang, messages.length, translateEffect]);

  const refsCurrent = { messages, singleOrBatchTrans };
  const refs = useRef(refsCurrent);
  refs.current = refsCurrent;

  return {
    conversation,
    keyMsgId,
    messages: uiMessages,
    fetch,
    fetchSbs,
    preference,
    preferBuyerLang,
    setPreference,
    isSameLang,
    singleOrBatchTrans,
    sysLang,
    nouseMsgList,
  };
};
export default useChatView;
